import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { formatCompanyDate } from '../utils/companyDateFormat';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Upload,
  X,
  Loader2,
  Settings2,
} from 'lucide-react';
import { jobApplicantApi, jobApplicantListValueApi } from '../services/api';
import type {
  JobApplicant,
  JobApplicantListValue,
  JobApplicantLovCategory,
  JobApplicantUpsertRequest,
} from '../types';
import { ImportJobApplicantsModal } from '../components/ImportJobApplicantsModal';
import {
  JobApplicantLovManageModal,
  JOB_APPLICANT_LOV_LABELS,
} from '../components/JobApplicantLovManageModal';

const emptyForm = (): JobApplicantUpsertRequest => ({
  firstName: '',
  lastName: '',
  applyingFor: '',
  sourceOfApplication: '',
  dateApplied: '',
  emailAddress: '',
  phoneNumber: '',
  cvApplicationLink: '',
  availabilityNoticePeriod: '',
  initialScreenPassed: '',
  applicationScore: null,
  invitedForInterview: '',
  applicationStatus: '',
  notes: '',
  reasonForDecision: '',
  reference1Name: '',
  reference1ContactDetails: '',
});

const LOV_FIELDS: { key: JobApplicantLovCategory; formKey: keyof JobApplicantUpsertRequest }[] = [
  { key: 'applying_for', formKey: 'applyingFor' },
  { key: 'source_of_application', formKey: 'sourceOfApplication' },
  { key: 'initial_screen_passed', formKey: 'initialScreenPassed' },
  { key: 'invited_for_interview', formKey: 'invitedForInterview' },
  { key: 'application_status', formKey: 'applicationStatus' },
  { key: 'reason_for_decision', formKey: 'reasonForDecision' },
];

function formatDate(value?: string | null): string {
  if (!value) return '—';
  try {
    return formatCompanyDate(value);
  } catch {
    return value;
  }
}

function formatFullName(row: JobApplicant): string {
  if (row.fullName?.trim()) return row.fullName.trim();
  const last = row.lastName?.trim();
  return last ? `${row.firstName} ${last}` : row.firstName;
}

function toDateInput(value?: string | null): string {
  if (!value) return '';
  try {
    return format(parseISO(value), 'yyyy-MM-dd');
  } catch {
    return value.slice(0, 10);
  }
}

interface LovSelectProps {
  category: JobApplicantLovCategory;
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  onManage: () => void;
}

const LovSelect: React.FC<LovSelectProps> = ({ category, label, value, options, onChange, onManage }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-neutral-600">{label}</label>
    <div className="flex items-center gap-1">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 rounded-md border border-neutral-300 px-3 py-2.5 text-base"
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={onManage}
        className="shrink-0 rounded-md border border-neutral-300 p-2 text-neutral-500 hover:bg-neutral-100 hover:text-blue-600"
        title={`Manage ${JOB_APPLICANT_LOV_LABELS[category]} values`}
        aria-label={`Manage ${JOB_APPLICANT_LOV_LABELS[category]} values`}
      >
        <Settings2 className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const JobApplicantTracking: React.FC = () => {
  const [applicants, setApplicants] = useState<JobApplicant[]>([]);
  const [listValues, setListValues] = useState<JobApplicantListValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<JobApplicant | null>(null);
  const [formData, setFormData] = useState<JobApplicantUpsertRequest>(emptyForm());
  const [lovModalCategory, setLovModalCategory] = useState<JobApplicantLovCategory | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterApplyingFor, setFilterApplyingFor] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterInitialScreen, setFilterInitialScreen] = useState('');
  const [filterInvited, setFilterInvited] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const lovOptions = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const item of listValues) {
      if (!map[item.category]) map[item.category] = [];
      map[item.category].push(item.value);
    }
    return map;
  }, [listValues]);

  const loadListValues = useCallback(async () => {
    const rows = await jobApplicantListValueApi.getAll();
    setListValues(rows);
  }, []);

  const loadApplicants = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await jobApplicantApi.getAll({
        search: searchTerm.trim() || undefined,
        applyingFor: filterApplyingFor || undefined,
        sourceOfApplication: filterSource || undefined,
        applicationStatus: filterStatus || undefined,
        initialScreenPassed: filterInitialScreen || undefined,
        invitedForInterview: filterInvited || undefined,
        dateAppliedFrom: filterDateFrom || undefined,
        dateAppliedTo: filterDateTo || undefined,
      });
      setApplicants(rows);
    } catch {
      alert('Failed to load applicants.');
    } finally {
      setLoading(false);
    }
  }, [
    searchTerm,
    filterApplyingFor,
    filterSource,
    filterStatus,
    filterInitialScreen,
    filterInvited,
    filterDateFrom,
    filterDateTo,
  ]);

  useEffect(() => {
    void loadListValues();
  }, [loadListValues]);

  useEffect(() => {
    void loadApplicants();
  }, [loadApplicants]);

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm());
    setShowForm(true);
  };

  const openEdit = (row: JobApplicant) => {
    setEditing(row);
    setFormData({
      firstName: row.firstName,
      lastName: row.lastName ?? '',
      applyingFor: row.applyingFor ?? '',
      sourceOfApplication: row.sourceOfApplication ?? '',
      dateApplied: toDateInput(row.dateApplied),
      emailAddress: row.emailAddress ?? '',
      phoneNumber: row.phoneNumber ?? '',
      cvApplicationLink: row.cvApplicationLink ?? '',
      availabilityNoticePeriod: row.availabilityNoticePeriod ?? '',
      initialScreenPassed: row.initialScreenPassed ?? '',
      applicationScore: row.applicationScore ?? null,
      invitedForInterview: row.invitedForInterview ?? '',
      applicationStatus: row.applicationStatus ?? '',
      notes: row.notes ?? '',
      reasonForDecision: row.reasonForDecision ?? '',
      reference1Name: row.reference1Name ?? '',
      reference1ContactDetails: row.reference1ContactDetails ?? '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName.trim()) {
      alert('First name is required.');
      return;
    }
    setSaving(true);
    try {
      const payload: JobApplicantUpsertRequest = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName?.trim() || null,
        applicationScore:
          formData.applicationScore === null || formData.applicationScore === undefined
            ? null
            : Number(formData.applicationScore),
      };
      if (editing) {
        await jobApplicantApi.update(editing.id, payload);
      } else {
        await jobApplicantApi.create(payload);
      }
      setShowForm(false);
      await loadApplicants();
    } catch {
      alert('Failed to save applicant.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: JobApplicant) => {
    if (!window.confirm(`Delete applicant "${formatFullName(row)}" (${row.applicantNumber})?`)) return;
    try {
      await jobApplicantApi.delete(row.id);
      await loadApplicants();
    } catch {
      alert('Failed to delete applicant.');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterApplyingFor('');
    setFilterSource('');
    setFilterStatus('');
    setFilterInitialScreen('');
    setFilterInvited('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  return (
    <div className="w-full max-w-[82%] mx-auto p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Job Applicant Tracking</h1>
          <p className="text-base text-neutral-600">
            Track applications, screening progress, interviews, and hiring decisions.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-base hover:bg-neutral-50"
          >
            <Upload className="h-4 w-4" />
            Bulk import
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-base text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add applicant
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-base font-medium text-neutral-700">
          <Search className="h-4 w-4" />
          Search &amp; filters
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm text-neutral-600">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name, email, phone, notes..."
              className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-neutral-600">Applying for</label>
            <select
              value={filterApplyingFor}
              onChange={(e) => setFilterApplyingFor(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
            >
              <option value="">All</option>
              {(lovOptions.applying_for ?? []).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-neutral-600">Source</label>
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
            >
              <option value="">All</option>
              {(lovOptions.source_of_application ?? []).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-neutral-600">Application status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
            >
              <option value="">All</option>
              {(lovOptions.application_status ?? []).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-neutral-600">Initial screen passed?</label>
            <select
              value={filterInitialScreen}
              onChange={(e) => setFilterInitialScreen(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
            >
              <option value="">All</option>
              {(lovOptions.initial_screen_passed ?? []).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-neutral-600">Invited for interview?</label>
            <select
              value={filterInvited}
              onChange={(e) => setFilterInvited(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
            >
              <option value="">All</option>
              {(lovOptions.invited_for_interview ?? []).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-neutral-600">Date applied from</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-neutral-600">Date applied to</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
            />
          </div>
        </div>
        <div className="mt-3">
          <button type="button" onClick={clearFilters} className="text-base text-blue-600 hover:underline">
            Clear filters
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : applicants.length === 0 ? (
          <div className="py-16 text-center text-base text-neutral-500">No applicants found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-base">
              <thead className="bg-neutral-50 text-left text-sm font-semibold uppercase tracking-wide text-neutral-600">
                <tr>
                  <th className="px-4 py-3">Applicant ID</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Applying for</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Date applied</th>
                  <th className="px-4 py-3">Screen</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Interview</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applicants.map((row) => (
                  <tr key={row.id} className="border-t hover:bg-neutral-50">
                    <td className="px-4 py-3 font-mono text-sm text-neutral-700">{row.applicantNumber}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-neutral-900">{formatFullName(row)}</div>
                      {row.emailAddress && <div className="text-sm text-neutral-500">{row.emailAddress}</div>}
                    </td>
                    <td className="px-4 py-3">{row.applyingFor ?? '—'}</td>
                    <td className="px-4 py-3">{row.sourceOfApplication ?? '—'}</td>
                    <td className="px-4 py-3">{formatDate(row.dateApplied)}</td>
                    <td className="px-4 py-3">{row.initialScreenPassed ?? '—'}</td>
                    <td className="px-4 py-3">{row.applicationScore ?? '—'}</td>
                    <td className="px-4 py-3">{row.invitedForInterview ?? '—'}</td>
                    <td className="px-4 py-3">{row.applicationStatus ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="rounded p-1 text-blue-600 hover:bg-blue-50"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(row)}
                          className="rounded p-1 text-red-600 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-xl font-semibold">
                {editing ? 'Edit applicant' : 'Add applicant'}
              </h3>
              <button type="button" onClick={() => setShowForm(false)} className="rounded p-1 hover:bg-neutral-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={(e) => void handleSubmit(e)} className="flex-1 overflow-y-auto p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  {editing && (
                    <div className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
                      <label className="mb-1 block text-sm font-medium text-neutral-600">Applicant ID</label>
                      <div className="font-mono text-base text-neutral-800">{editing.applicantNumber}</div>
                      <p className="mt-1 text-sm text-neutral-500">Assigned automatically when the record was created.</p>
                    </div>
                  )}
                  {!editing && (
                    <p className="mb-4 text-sm text-neutral-500">
                      Applicant ID will be assigned automatically (e.g. APP-2026-000001).
                    </p>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-600">First name *</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-600">Last name</label>
                  <input
                    type="text"
                    value={formData.lastName ?? ''}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>

                {LOV_FIELDS.map(({ key, formKey }) => (
                  <LovSelect
                    key={key}
                    category={key}
                    label={JOB_APPLICANT_LOV_LABELS[key]}
                    value={(formData[formKey] as string) ?? ''}
                    options={lovOptions[key] ?? []}
                    onChange={(v) => setFormData({ ...formData, [formKey]: v })}
                    onManage={() => setLovModalCategory(key)}
                  />
                ))}

                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-600">Date applied</label>
                  <input
                    type="date"
                    value={formData.dateApplied ?? ''}
                    onChange={(e) => setFormData({ ...formData, dateApplied: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-600">Application score</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={formData.applicationScore ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        applicationScore: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-600">Email address</label>
                  <input
                    type="email"
                    value={formData.emailAddress ?? ''}
                    onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-600">Phone number</label>
                  <input
                    type="text"
                    value={formData.phoneNumber ?? ''}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-neutral-600">
                    Link to CV/Application form
                  </label>
                  <input
                    type="url"
                    value={formData.cvApplicationLink ?? ''}
                    onChange={(e) => setFormData({ ...formData, cvApplicationLink: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-neutral-600">
                    Availability / Notice Period
                  </label>
                  <input
                    type="text"
                    value={formData.availabilityNoticePeriod ?? ''}
                    onChange={(e) => setFormData({ ...formData, availabilityNoticePeriod: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-600">Reference 1 Name</label>
                  <input
                    type="text"
                    value={formData.reference1Name ?? ''}
                    onChange={(e) => setFormData({ ...formData, reference1Name: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-600">R1 Contact details</label>
                  <input
                    type="text"
                    value={formData.reference1ContactDetails ?? ''}
                    onChange={(e) => setFormData({ ...formData, reference1ContactDetails: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-neutral-600">Notes</label>
                  <textarea
                    rows={3}
                    value={formData.notes ?? ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-base"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2 border-t pt-4">
                <button type="button" onClick={() => setShowForm(false)} className="rounded-md px-4 py-2.5 text-base hover:bg-neutral-100">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-base text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editing ? 'Save changes' : 'Create applicant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ImportJobApplicantsModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImportCommitted={() => {
          void loadApplicants();
        }}
      />

      {lovModalCategory && (
        <JobApplicantLovManageModal
          open={!!lovModalCategory}
          category={lovModalCategory}
          onClose={() => setLovModalCategory(null)}
          onChanged={() => void loadListValues()}
        />
      )}
    </div>
  );
};

export default JobApplicantTracking;
